import { useEffect, useState, useRef } from 'react'
import { getImage } from '../exif-utils'

/**
 *
 * @typedef ImageState
 * @property {HTMLImageElement} loadedImage
 * @property {import('ol/extent').Extent} extent
 * @property {string} status
 * @property {Error} error
 */

/**
 *
 * @param {*} imageFeature
 * @param {*} getImageData
 * @returns {ImageState}
 */
export default function useImage(imageFeature, getImageData) {
  const [status, setStatus] = useState('idle')
  const [error, setError] = useState(null)
  const [loadedImage, setLoadedImage] = useState(null)
  const [extent, setExtent] = useState(null)
  const loadPromiseRef = useRef(null)

  useEffect(() => {
    let aborted = false
    let url
    if (imageFeature && getImageData) {
      setStatus('loading')
      const loadPromise = getImage(imageFeature, image => getImageData(image), {
        revokeObjectURL: false,
      })
        .then(image => {
          if (!aborted) {
            image.onload = () => {
              if (aborted || loadPromise !== loadPromiseRef.current) {
                // The current load promise is not our load promise;
                // this means another image started loading after this,
                // and we should just quietly ignore this request.
                return
              }
              url = image.src
              setLoadedImage(image)
              setExtent([0, -image.height, image.width, 0])
              setStatus('idle')
              setError(null)
            }

            image.onerror = error => {
              if (!aborted && loadPromise === loadPromiseRef.current) {
                setError(error)
                setStatus('idle')
              }
            }
          }
        })
        .catch(error => {
          if (!aborted) {
            setError(error)
            setStatus('idle')
          }
        })

      loadPromiseRef.current = loadPromise
    }
    return () => {
      aborted = true
      if (url) {
        window.URL.revokeObjectURL(url)
      }
    }
  }, [getImageData, imageFeature])
  return { loadedImage, extent, status, error }
}
